import {validateSelectedZipCodeAddress} from '@web/actions/deliveryInfo'
import {DELIVERY_MEMO} from '@web/core/constants'
import {decodeHtmlEntities as decode} from '@web/helpers/htmlEntities'
import {isArrivalGuarantee} from '@web/helpers/policies'
import {ZipCodeValidationStatus} from '@web/slices/deliveryInfo/slice'
import makeSlice from '@web/slices/deliveryInfo/sliceMaker'

import {fetchSubscription, initSubscription, restoreSubscription} from '../../actions'
import {DeliveryMemo} from '../../apis/interfaces'
import {convertDeliveryAddress} from '../../utils'

const slice = makeSlice({
    extraReducers(builder) {
        builder
            .addCase(
                fetchSubscription.fulfilled,
                (
                    state,
                    {
                        payload: {
                            subscriptionViewResult: {deliveryAddressBook, products},
                        },
                    },
                ) => {
                    if (deliveryAddressBook) {
                        const {
                            defaultDeliveryAddress,
                            onVirtualPhoneNumber,
                            restrictedVirtualPhoneNumbers,
                            recentUsedDeliveryMemosReuse,
                        } = deliveryAddressBook

                        const deliveryAddress = convertDeliveryAddress(defaultDeliveryAddress)

                        state.selected = {
                            addressInfo: deliveryAddress,
                            recipient: 'NORMAL',
                        }

                        if (deliveryAddress?.locationType) {
                            state.pickup.location.type = deliveryAddress.locationType
                            state.pickup.location.content = deliveryAddress.locationDescription ?? ''
                        }
                        if (deliveryAddress?.entryMethodType) {
                            state.pickup.entryMethod.type = deliveryAddress.entryMethodType
                            state.pickup.entryMethod.content = deliveryAddress.entryDescription ?? ''
                        }

                        state.pickup.alwaysUses = true

                        state.virtualPhoneNumber.onVirtualPhoneNumber = onVirtualPhoneNumber
                        state.virtualPhoneNumber.restrictedVirtualPhoneNumbers = restrictedVirtualPhoneNumbers

                        const memoList = recentUsedDeliveryMemosReuse.map((deliveryMemo: DeliveryMemo) => {
                            const {memo, memoSeq, reuseMemo, template} = deliveryMemo
                            return {
                                value: decode(memo),
                                reuse: reuseMemo,
                                template,
                                sequenceNumber: memoSeq,
                            }
                        })
                        state.memo.list = memoList

                        // 메모 중 다음에도 사용할 메모가 있는 경우 통합메모에 초기화
                        const memoToReuse = memoList?.find(({reuse}: {reuse: boolean}) => reuse)
                        if (memoToReuse) {
                            state.memo.individuallyInput = {
                                type: memoToReuse.template ? DELIVERY_MEMO.COMMON : DELIVERY_MEMO.WRITTEN,
                                value: memoToReuse.value,
                            }
                            state.memo.saveAsDefaultMemo = true
                        }

                        state.hasDefaultAddress = deliveryAddress?.isPrimaryAddress ?? false

                        state.hasArrivalGuaranteeProduct = products.some(({deliveryAttributeType}) =>
                            isArrivalGuarantee(deliveryAttributeType),
                        )
                        state.hasOverseasDeliveryProduct = products.some(({overseasDelivery}) => overseasDelivery)
                    }
                },
            )
            .addCase(restoreSubscription.fulfilled, (state, {payload: {storedState}}) => {
                state.selected = storedState.deliveryInfo.selected
                state.memo = storedState.deliveryInfo.memo
                state.virtualPhoneNumber = storedState.deliveryInfo.virtualPhoneNumber
                if (storedState.deliveryInfo.pickup) {
                    state.pickup = storedState.deliveryInfo.pickup
                }
            })
            .addCase(initSubscription.fulfilled, (state, {payload}) => {
                state.zipCodeValidation.status = ZipCodeValidationStatus.READY
                state.zipCodeValidation.validateCase = payload.storedByStorage ? 'REFRESH' : 'INIT'
            })
            .addCase(validateSelectedZipCodeAddress.fulfilled, (state, {payload}) => {
                state.zipCodeValidation.status = ZipCodeValidationStatus.IDLE
                state.zipCodeValidation.validationFailType = payload.validationFailType
            })
            .addCase(validateSelectedZipCodeAddress.rejected, (state) => {
                state.zipCodeValidation.status = ZipCodeValidationStatus.IDLE
            })
    },
})

export default slice.reducer
export const actions = slice.actions
